import { Box, CircularProgress, styled, Typography, keyframes } from '@mui/material';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  steps: string[];
  stepDurationMs?: number;
  onDone: () => void;
}

export function ThinkingCard({ steps, stepDurationMs = 1400, onDone }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    // Infinity means "sticky" — the caller is wrapping real async work of
    // unknown duration and will swap/clear `thinking` itself once it
    // resolves, rather than the card auto-advancing on a fake timer.
    if (!Number.isFinite(stepDurationMs)) return;
    const jitter = stepDurationMs * (0.7 + Math.random() * 0.6);
    const timer = setTimeout(() => {
      if (activeIndex >= steps.length - 1) {
        onDone();
      } else {
        setActiveIndex((i) => i + 1);
      }
    }, jitter);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  return (
    <ThinkingBox>
      {steps.slice(0, activeIndex + 1).map((label, i) => {
        const isActive = i === activeIndex;
        return (
          <Row key={i}>
            {isActive ? <Dot /> : <Check size={16} color={'#16a34a'} />}
            <Typography sx={{ fontSize: 13, color: isActive ? '#334155' : '#64748b' }}>{label}</Typography>
          </Row>
        );
      })}
    </ThinkingBox>
  );
}

export function ThinkingElapsedRow() {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <ElapsedRow>
      <CircularProgress size={12} sx={{ color: '#094451' }} />
      <Typography sx={{ fontSize: 13, color: '#64748b' }}>
        {seconds}s &#8226; <i>Thinking...</i>
      </Typography>
    </ElapsedRow>
  );
}

// ─── Styled — matches apps/ai-consumer chat-bubble-stream.tsx ThinkingCard ────

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.7); }
`;

const ThinkingBox = styled(Box)({
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '12px 16px',
  minWidth: 220,
  width: 'fit-content',
});

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '3px 0',
});

const ElapsedRow = styled(Box)({
  background: 'transparent',
  borderRadius: 12,
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  width: 'fit-content',
});

const Dot = styled(Box)({
  width: 8,
  height: 8,
  margin: 4,
  borderRadius: '50%',
  background: '#094451',
  flexShrink: 0,
  animation: `${pulse} 1.1s ease-in-out infinite`,
});
